/**
 * Where the session lives between page loads.
 *
 * `localStorage`, deliberately and with a known trade-off: it is readable by any
 * script running on this origin, so an XSS bug can lift the token. The
 * alternative — an httpOnly cookie — needs the API to set cookies, and the API
 * is a separate service issuing bearer tokens (`docs/API.md` §1.2). Moving to
 * cookies is an API-side decision; until it is made, this module is the single
 * place the choice is expressed, so changing it later touches one file.
 *
 * Every access is wrapped: Safari private mode and browsers configured to block
 * site data throw on `localStorage` access rather than returning null.
 */

const KEY = 'entiovicrm.session';

/**
 * @typedef {object} StoredSession
 * @property {string} accessToken
 * @property {string} refreshToken
 * @property {number} expiresAt  epoch ms, for pre-emptive refresh
 * @property {string} userId
 * @property {string} sessionId
 */

/** @returns {StoredSession|null} the stored session, or null if absent or unreadable */
export function readStoredSession() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.accessToken ? parsed : null;
  } catch {
    return null;
  }
}

/** @param {StoredSession} session */
export function writeStoredSession(session) {
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    /* Storage blocked. The session still works for this tab — it is held in
       memory by AuthContext — it just will not survive a reload. */
  }
}

export function clearStoredSession() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Build the stored shape from a `POST /auth/login` response body.
 * @param {object} body the API's AuthenticatedSession
 */
export function sessionFromLogin(body) {
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: Date.now() + body.expires_in * 1000,
    userId: body.user.id,
    sessionId: body.session.id,
  };
}

/* -------------------------------------------------------------------------- */
/* UI preferences — per-browser, never sent to the server                      */
/* -------------------------------------------------------------------------- */

const NAV_KEY = 'entiovicrm.nav';

/**
 * Sidebar collapsed state and which groups are folded shut.
 *
 * Kept separate from the session so signing out does not reset someone's
 * navigation layout, and separate from `iam.app_user.ui_prefs` because this is a
 * per-device convenience — a collapsed sidebar on a laptop should not collapse
 * it on a desktop.
 */
export function readNavState() {
  try {
    const raw = localStorage.getItem(NAV_KEY);
    if (!raw) return { collapsed: false, closed: [] };
    const parsed = JSON.parse(raw);
    return {
      collapsed: Boolean(parsed.collapsed),
      closed: Array.isArray(parsed.closed) ? parsed.closed : [],
    };
  } catch {
    return { collapsed: false, closed: [] };
  }
}

/** @param {{collapsed:boolean, closed:string[]}} state */
export function writeNavState(state) {
  try {
    localStorage.setItem(NAV_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}
